import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useSelector } from 'react-redux';
import { getNotes } from '../services/projectApi';
import { generateUniqueId } from '../utils/generateUniqueId';
import {
  applyAuthorFilterToScene,
  applyAuthorFilterToObject,
  subscribeAuthorFilter,
} from '../utils/noteAuthorFilter';
import { drawCalloutCanvas } from '../utils/drawCalloutCanvas';

const POLL_INTERVAL = 5000; // 5 seconds

const LEADER_COLOR = 0x00ccff;
const TEXT_COLOR = '#00ccff';

/**
 * Polls the server for new callout notes and renders them in the scene.
 * Skips notes that were created locally (already in scene).
 */
const useCalloutSync = (scene, camera) => {
  const projectId = useSelector(state => state.project.projectId);
  const shareAccess = useSelector(state => state.project.shareAccess);

  // Use refs to keep latest values accessible in interval without re-creating it
  const sceneRef = useRef(scene);
  const cameraRef = useRef(camera);
  const projectIdRef = useRef(projectId);
  const shareTokenRef = useRef(shareAccess?.token);
  const lastPollRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const intervalRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { sceneRef.current = scene; }, [scene]);
  useEffect(() => { cameraRef.current = camera; }, [camera]);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  useEffect(() => { shareTokenRef.current = shareAccess?.token; }, [shareAccess]);

  // Calculate nearest box edge point for leader line
  const getNearestBoxEdgePoint = (target, boxPos, w, h) => {
    const left = boxPos.x;
    const right = boxPos.x + w;
    const top = boxPos.y;
    const bottom = boxPos.y - h;

    const cx = Math.max(left, Math.min(right, target.x));
    const cy = Math.max(bottom, Math.min(top, target.y));

    if (cx > left && cx < right && cy > bottom && cy < top) {
      const dLeft = cx - left;
      const dRight = right - cx;
      const dTop = top - cy;
      const dBottom = cy - bottom;
      const minDist = Math.min(dLeft, dRight, dTop, dBottom);
      if (minDist === dLeft) return new THREE.Vector3(left, cy, 0);
      if (minDist === dRight) return new THREE.Vector3(right, cy, 0);
      if (minDist === dTop) return new THREE.Vector3(cx, top, 0);
      return new THREE.Vector3(cx, bottom, 0);
    }
    return new THREE.Vector3(cx, cy, 0);
  };

  // Create a callout group from server note data
  const createCalloutFromNote = (note, cam) => {
    if (!cam) return null;
    const group = new THREE.Group();

    const target = new THREE.Vector3(note.targetPoint.x, note.targetPoint.y, note.targetPoint.z || 0);
    const boxPos = new THREE.Vector3(note.boxPosition.x, note.boxPosition.y, note.boxPosition.z || 0);

    // Header — yazar adı + (varsa) rol
    const headerParts = [];
    if (note.authorName) headerParts.push(note.authorName);
    if (note.authorRole) headerParts.push(note.authorRole);
    const headerText = headerParts.join(' — ');

    const { canvas, width: cssW, height: cssH } = drawCalloutCanvas({
      header: headerText,
      text: note.rawText || '',
      color: TEXT_COLOR,
    });

    const viewHeight = cam.top ? (cam.top - cam.bottom) : 500;
    const spriteWorldWidth = viewHeight * 0.2;
    const spriteWorldHeight = spriteWorldWidth * (cssH / cssW);

    // Leader line -> nearest box edge
    const edgePoint = getNearestBoxEdgePoint(target, boxPos, spriteWorldWidth, spriteWorldHeight);
    const leaderGeometry = new THREE.BufferGeometry().setFromPoints([target, edgePoint]);
    const leaderMaterial = new THREE.LineBasicMaterial({ color: LEADER_COLOR });
    group.add(new THREE.Line(leaderGeometry, leaderMaterial));

    // Target marker — sabit 2 birim yarıçap
    const markerGeometry = new THREE.CircleGeometry(2, 16);
    const markerMaterial = new THREE.MeshBasicMaterial({ color: LEADER_COLOR });
    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
    marker.position.copy(target);
    group.add(marker);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 16;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, alphaTest: 0.05 });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(spriteWorldWidth, spriteWorldHeight, 1);
    sprite.center.set(0, 1);
    sprite.position.copy(boxPos);
    group.add(sprite);

    group.userData = {
      id: note.calloutId || generateUniqueId('callout'),
      type: 'callout',
      isSelectable: true,
      rawText: note.rawText,
      targetPoint: note.targetPoint,
      boxPosition: note.boxPosition,
      layerName: note.layerName || 'NOTES',
      authorName: note.authorName,
      authorRole: note.authorRole,
      authorUserId: note.authorUserId || null,
      syncedFromServer: true,
    };

    // Yeni eklenen callout — paydaş filtresine göre görünürlüğü uygula
    applyAuthorFilterToObject(group);

    return group;
  };

  // Stable poll function using refs
  const poll = useCallback(async () => {
    const pid = projectIdRef.current;
    const sc = sceneRef.current;
    const cam = cameraRef.current;
    if (!pid || !sc || !cam) {
      return;
    }

    try {
      const notes = await getNotes(pid, lastPollRef.current || undefined, shareTokenRef.current);
      if (!notes || notes.length === 0) return;

      // Update last poll time
      lastPollRef.current = notes[notes.length - 1].createdAt;

      // Get existing callout IDs in scene
      const existingIds = new Set();
      sc.traverse((obj) => {
        if (obj.userData?.type === 'callout' && obj.userData.id) {
          existingIds.add(obj.userData.id);
        }
      });

      for (const note of notes) {
        const noteCalloutId = note.calloutId;
        if (noteCalloutId && (existingIds.has(noteCalloutId) || knownIdsRef.current.has(noteCalloutId))) {
          knownIdsRef.current.add(noteCalloutId);
          continue;
        }

        const group = createCalloutFromNote(note, cam);
        if (group) {
          sc.add(group);
          knownIdsRef.current.add(group.userData.id);
        }
      }
    } catch (err) {
      console.warn('[CalloutSync] Poll hatasi:', err.message);
    }
  }, []); // No deps — uses refs

  // Start/stop polling when projectId changes
  useEffect(() => {
    if (!projectId || !scene || !camera) return;

    // Initial poll after a short delay (let scene settle)
    const initTimer = setTimeout(() => poll(), 1000);

    intervalRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      clearTimeout(initTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [projectId, scene, camera, poll]);

  // Reset when project changes
  useEffect(() => {
    lastPollRef.current = null;
    knownIdsRef.current.clear();
  }, [projectId]);

  // Paydaş filtresi değiştiğinde sahnedeki callout'ların visibility'sini güncelle
  useEffect(() => {
    if (!scene) return;
    // İlk uygulama
    applyAuthorFilterToScene(scene);
    // Subscribe — filtre değişimine reaktif
    const unsub = subscribeAuthorFilter(() => {
      applyAuthorFilterToScene(sceneRef.current);
    });
    return unsub;
  }, [scene]);
};

export default useCalloutSync;
